<template>
  <div class="m-image-request-and-copyright__copyright">
    <!-- VisualItem | HMO Copyright Statement -->
    <div class="m-image-request-and-copyright__image-copyright">
      <GuiLink
        ref="rightsLink"
        :href="rightsLink"
        with-icon="before"
        :icon-name="iconName"
        class="m-image-request-and-copyright__copyright-link"
      >
        {{ rightsStatementText }}
      </GuiLink>
      <span v-if="copyrightStatement" ref="copyrightStatement">
        /
        {{ copyrightStatement }}
      </span>
    </div>
  </div>
</template>

<script>
import { GuiLink, useLocalization } from "@thegetty-private/getty-ui";
import rightsStatements from "@/data/rights-statements";
import {
  summarizeLicenses,
  OPEN_CONTENT
} from "@thegetty-private/linked-data.js";

export default {
  name: "CopyrightStatement",
  components: {
    GuiLink
  },

  props: {
    /**
     * The Human Made Object Copyright Statement
     */
    objectCopyrightStatement: {
      type: String,
      required: false,
      default: undefined
    },
    /**
     * The Visual Item JSON-LD object for the selected item
     */
    mediaInfo: {
      type: Object,
      required: true
    }
  },
  setup(props) {
    return {
      ...useLocalization({
        baseLocaleKey: props?.baseLocaleKey
      })
    };
  },
  data() {
    return {
      /**
       * The url for creativecommons.org which is the baselink for a CC0
       * rights statement link
       */
      creativecommonsLink: "https://creativecommons.org/",
      /**
       * The url for rightsstatements.org which is the baselink for
       * all rights statements links
       */
      rightsStatementsOrgLink: "https://rightsstatements.org/"
    };
  },
  computed: {
    /**
     * Gets the copyright statement from the visualitem or HMO
     *
     * @returns {string} the copyright statement
     */
    copyrightStatement() {
      // check first for visualItem copyright statement
      let copyrightStatement = this.mediaInfo.copyright;

      if (copyrightStatement) {
        return this.translatePart("object_page.image_copyright", {
          statement: copyrightStatement
        });
      }

      // if there is none from the visualItem, check the HMO
      return this.objectCopyrightStatement;
    },
    /**
     * Whether or not the image is under copyright
     *
     * @returns {boolean}
     */
    inCopyright() {
      let rightsStatementId = this.mediaInfo?.rightsStatementId;
      if (rightsStatementId) {
        let cleanRightsId = rightsStatementId.replace("https", "http");
        // if the rights statement ID doesn't include the No Copyright url, return true
        return summarizeLicenses([cleanRightsId]) != OPEN_CONTENT;
      }
      return true;
    },

    /**
     * The name of the icon to display beside the rights statement
     *
     * @returns {string} the icon name
     */
    iconName() {
      if (!this.rightsStatement) {
        return undefined;
      }
      let iconName;
      switch (this.rightsStatement) {
        case "NO_COPYRIGHT":
          iconName = "CopyrightNone";
          break;
        case "UNDETERMINED":
        case "NOT_EVALUATED":
          iconName = "CopyrightOther";
          break;
        case "ZERO":
          iconName = "CC0";
          break;
        case "IN_COPYRIGHT":
        case "IN_COPYRIGHT_UNIDENTIFIABLE":
        default:
          iconName = "Copyright";
          break;
      }

      return iconName;
    },
    /**
     * The link to a reference point for a rights statement
     *
     * @returns {string}
     */
    rightsLink() {
      if (!this.rightsStatement) {
        return undefined;
      }

      if (this.rightsStatement == "ZERO") {
        return (
          this.creativecommonsLink + rightsStatements[this.rightsStatement]
        );
      }

      return (
        this.rightsStatementsOrgLink + rightsStatements[this.rightsStatement]
      );
    },
    /** The type of rights statement to use for this image
     *
     * @returns {string} (ex. "IN_COPYRIGHT", "NO_COPYRIGHT", "UNDETERMINED")
     */
    rightsStatement() {
      let rightsStatementId = this.mediaInfo?.rightsStatementId;
      if (
        rightsStatementId == undefined ||
        rightsStatementId.includes(rightsStatements.IN_COPYRIGHT_UNIDENTIFIABLE)
      ) {
        return "IN_COPYRIGHT_UNIDENTIFIABLE";
      }

      if (rightsStatementId.includes(rightsStatements.UNDETERMINED)) {
        return "UNDETERMINED";
      }

      if (rightsStatementId.includes(rightsStatements.NOT_EVALUATED)) {
        return "NOT_EVALUATED";
      }

      if (rightsStatementId.includes(rightsStatements.ZERO)) {
        return "ZERO";
      }

      if (this.inCopyright) {
        return "IN_COPYRIGHT";
      }

      return "NO_COPYRIGHT";
    },
    /**
     * The translated text for the rights statement
     *
     * @returns {string}
     */
    rightsStatementText() {
      if (!this.rightsStatement) {
        return undefined;
      }
      return this.translatePart(`object_page.${this.rightsStatement}`);
    }
  },
  methods: {}
};
</script>
